import { useEffect, type ComponentType, type ReactNode } from 'react';
import { ExternalLink, Mail, Phone } from 'lucide-react';
import { FaFacebook, FaInstagram, FaLinkedin, FaYoutube, FaWhatsapp } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@/hooks/useLanguage';
import { useAuth } from '@/components/AuthProvider';
import { getAccessToken } from '@/services/authService';
import {
  buildFooterNavSections,
  type FooterNavLink,
} from '@/components/landing/footerNavConfig';
import {
  consumePendingNavigation,
  setPendingNavigation,
} from '@/utils/authPendingNavigation';

interface FooterSectionProps {
  showAuthModal?: boolean;
  setShowAuthModal?: (show: boolean) => void;
  setAuthMode?: (mode: 'login' | 'register') => void;
}

type SocialLink = {
  icon: ComponentType<{ className?: string }>;
  href: string;
  label: string;
  className: string;
};

const ACCENT = '#22C55E';
const FOOTER_BG = '#0B121E';

/** Contact details are locale-independent — never pull from i18n. */
const FOOTER_CONTACT = {
  email: 'contact@hansaitechnosoft.com',
  phone: '+91 76809 38741',
  hours: 'Available hrs: 8 AM to 10 PM IST',
} as const;

/** Full-width row: leading cue + label */
const LINK_ROW_CLASS =
  'group flex w-full items-center gap-2.5 py-0.5 text-left text-sm text-white/85 outline-none transition-colors duration-200 hover:text-white focus-visible:text-white';

const SOCIAL_LINKS: SocialLink[] = [
  {
    icon: FaXTwitter,
    href: 'https://x.com/DuriRideSharing',
    label: 'X',
    className: 'bg-[#1a1a1a] text-white hover:bg-black',
  },
  {
    icon: FaFacebook,
    href: 'https://www.facebook.com/profile.php?id=61583917402483',
    label: 'Facebook',
    className: 'bg-[#1877F2] text-white hover:brightness-110',
  },
  {
    icon: FaInstagram,
    href: 'https://www.instagram.com/duriridesharing',
    label: 'Instagram',
    className:
      'bg-gradient-to-br from-[#f58529] via-[#dd2a7b] to-[#8134af] text-white hover:brightness-110',
  },
  {
    icon: FaLinkedin,
    href: 'https://www.linkedin.com/company/duriridesharing',
    label: 'LinkedIn',
    className: 'bg-[#0A66C2] text-white hover:brightness-110',
  },
  {
    icon: FaYoutube,
    href: 'https://www.youtube.com/@DuriRideSharing',
    label: 'YouTube',
    className: 'bg-[#FF0000] text-white hover:brightness-110',
  },
];

function FooterNavLinkItem({
  link,
  onAuthNavigate,
}: {
  link: FooterNavLink;
  onAuthNavigate: (path: string) => void;
}) {
  const content = (
    <>
      {link.external ? (
        <ExternalLink
          className="h-3.5 w-3.5 shrink-0 text-white/35 transition-colors duration-200 group-hover:text-[#22C55E]"
          aria-hidden
        />
      ) : (
        <span
          className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-white/40 transition-colors duration-200 group-hover:bg-[#22C55E]"
          aria-hidden
        />
      )}
      <span className="min-w-0 leading-snug">{link.name}</span>
    </>
  );

  if (link.requiresAuth) {
    return (
      <button
        type="button"
        className={`${LINK_ROW_CLASS} cursor-pointer border-none bg-transparent p-0`}
        onClick={() => onAuthNavigate(link.href)}
      >
        {content}
      </button>
    );
  }

  if (link.external) {
    return (
      <a href={link.href} target="_blank" rel="noopener noreferrer" className={LINK_ROW_CLASS}>
        {content}
      </a>
    );
  }

  return (
    <Link to={link.href} className={LINK_ROW_CLASS}>
      {content}
    </Link>
  );
}

function ContactRow({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-xs text-white/85 sm:text-sm">
      <span className="mt-0.5 shrink-0" style={{ color: ACCENT }}>
        {icon}
      </span>
      <span className="min-w-0 break-words leading-snug">{children}</span>
    </div>
  );
}

export const FooterSection = ({
  setShowAuthModal,
  setAuthMode,
}: FooterSectionProps = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const tFooter = (key: string) => t(key, 'footer');
  const { isAuthenticated } = useAuth();

  const isLoggedIn = isAuthenticated || !!getAccessToken();
  const linkSections = buildFooterNavSections(t);

  const handleNavigateWithAuth = (path: string) => {
    if (isLoggedIn) {
      navigate(path);
      return;
    }
    setPendingNavigation(path);
    setAuthMode?.('login');
    setShowAuthModal?.(true);
  };

  useEffect(() => {
    if (!isLoggedIn) return;
    const pendingPath = consumePendingNavigation();
    if (!pendingPath) return;
    const timer = window.setTimeout(() => navigate(pendingPath), 100);
    return () => window.clearTimeout(timer);
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    if (location.hash !== '#footer' && location.pathname !== '/footer') return;
    const footerElement = document.getElementById('footer');
    if (!footerElement) return;

    const timer = window.setTimeout(() => {
      const headerOffset = 100;
      const offsetPosition =
        footerElement.getBoundingClientRect().top + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }, 100);

    return () => window.clearTimeout(timer);
  }, [location.hash, location.pathname]);

  const copyrightYear = new Date().getFullYear().toString();

  return (
    <footer
      id="footer"
      className="text-white"
      style={{ backgroundColor: FOOTER_BG, scrollMarginTop: '100px' }}
    >
      <div className="mx-auto w-full max-w-[90rem] px-3 py-14 sm:px-4 lg:px-5 lg:py-16">
        {/* Brand is wider (~1.4fr); five link columns share the rest evenly */}
        <div className="grid grid-cols-2 gap-x-5 gap-y-10 sm:grid-cols-3 lg:grid-cols-[minmax(14rem,1.45fr)_repeat(5,minmax(0,1fr))] lg:gap-x-6">
          <div className="col-span-2 sm:col-span-3 lg:col-span-1">
            <img
              src="/Duri_Logo_Footer.png"
              alt="Duri - Ridesharing App"
              className="mb-5 h-[4.5rem] w-auto object-contain sm:h-20"
            />
            <p className="mb-5 max-w-[17rem] text-sm leading-relaxed text-white/70">
              {tFooter('brandDesc')}
            </p>
            <div className="mb-5 h-px w-16 bg-white/20" aria-hidden />
            <div className="space-y-3">
              <ContactRow icon={<Mail className="h-4 w-4" />}>
                {FOOTER_CONTACT.email}
              </ContactRow>
              <ContactRow icon={<FaWhatsapp className="h-4 w-4" />}>
                {FOOTER_CONTACT.phone}
              </ContactRow>
              <ContactRow icon={<Phone className="h-4 w-4" />}>
                {FOOTER_CONTACT.hours}
              </ContactRow>
            </div>
          </div>

          {linkSections.map((section) => (
            <nav key={section.id} aria-label={section.title} className="min-w-0">
              <h3 className="text-sm font-semibold leading-snug text-white sm:text-base">
                {section.title}
              </h3>
              <div
                className="mb-4 mt-2 h-0.5 w-7 rounded-full"
                style={{ backgroundColor: ACCENT }}
                aria-hidden
              />
              <ul className="space-y-2.5">
                {section.links.map((link) => (
                  <li key={`${section.id}-${link.name}`}>
                    <FooterNavLinkItem link={link} onAuthNavigate={handleNavigateWithAuth} />
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex w-full max-w-[90rem] flex-col items-center justify-between gap-4 px-3 py-5 sm:px-4 md:flex-row lg:px-5">
          <div className="flex flex-wrap items-center justify-center gap-3 text-center text-sm text-white/55 md:justify-start md:text-left">
            <span>{tFooter('copyrightText').replace('{year}', copyrightYear)}</span>
            <span className="hidden text-white/25 sm:inline" aria-hidden>
              |
            </span>
            <a href="https://hansaitechnosoft.com" target="_blank" rel="noopener noreferrer">
              <img
                src="/hansai-logo.png"
                alt="HansAI Techno Soft"
                loading="lazy"
                decoding="async"
                className="h-7 w-auto rounded-md bg-white object-contain px-1.5 py-0.5"
              />
            </a>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <span className="text-sm text-white/55">{tFooter('followUs')}</span>
            <div className="flex gap-2.5">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.href}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className={`flex h-9 w-9 items-center justify-center rounded-full transition-transform hover:scale-110 ${social.className}`}
                >
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
